/**
 * SurebetViewChampionshipFilter - Multi-select by event.championship.
 * Options from current surebets payload (+ persisted selections).
 */

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSurebet } from '../context/SurebetContext';
import { useTranslation } from '../../i18n/hooks';
import { collectChampionships } from '../viewFilters/surebetViewFilters';
import '../../bestOdds/components/BestOddsBookmakerFilter.css';

const SurebetViewChampionshipFilter = ({ className = '' }) => {
  const { t } = useTranslation();
  const {
    surebets,
    viewFilters,
    setSelectedChampionships
  } = useSurebet();

  const [localSearch, setLocalSearch] = useState('');

  const availableChampionships = useMemo(() => {
    const names = new Set(collectChampionships(surebets));

    if (Array.isArray(viewFilters?.selectedChampionships)) {
      viewFilters.selectedChampionships.forEach((name) => {
        if (name && String(name).trim()) {
          names.add(String(name));
        }
      });
    }

    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [surebets, viewFilters?.selectedChampionships]);

  const selectedChampionships = viewFilters?.selectedChampionships;
  const isAllSelected = selectedChampionships === null;

  const selectedCount = isAllSelected
    ? availableChampionships.length
    : (Array.isArray(selectedChampionships) ? selectedChampionships.length : 0);

  const isChampionshipSelected = useCallback((championship) => {
    if (selectedChampionships === null || !Array.isArray(selectedChampionships)) {
      return true;
    }
    return selectedChampionships.includes(championship);
  }, [selectedChampionships]);

  const filteredChampionships = useMemo(() => {
    const query = localSearch.trim().toLowerCase();
    if (!query) {
      return availableChampionships;
    }
    return availableChampionships.filter((name) =>
      name.toLowerCase().includes(query)
    );
  }, [availableChampionships, localSearch]);

  const handleSelectAll = useCallback(() => {
    setSelectedChampionships(null);
  }, [setSelectedChampionships]);

  const handleDeselectAll = useCallback(() => {
    setSelectedChampionships([]);
  }, [setSelectedChampionships]);

  const handleToggle = useCallback((championship) => {
    if (selectedChampionships === null || !Array.isArray(selectedChampionships)) {
      setSelectedChampionships(availableChampionships.filter((name) => name !== championship));
      return;
    }

    if (selectedChampionships.includes(championship)) {
      setSelectedChampionships(selectedChampionships.filter((name) => name !== championship));
      return;
    }

    const next = [...selectedChampionships, championship];
    const allSelected = availableChampionships.length > 0
      && availableChampionships.every((name) => next.includes(name));

    setSelectedChampionships(allSelected ? null : next);
  }, [availableChampionships, selectedChampionships, setSelectedChampionships]);

  if (availableChampionships.length === 0) {
    return null;
  }

  return (
    <div
      className={`best-odds-bookmaker-filter best-odds-bookmaker-filter--wide ${className}`.trim()}
      role="group"
      aria-label={t('surebets.viewFilters.championshipsAriaLabel')}
    >
      <div className="best-odds-bookmaker-filter__header">
        <span className="best-odds-bookmaker-filter__label">
          {t('surebets.viewFilters.championshipsLabel')}
          <span className="best-odds-bookmaker-filter__count">
            ({selectedCount}/{availableChampionships.length})
          </span>
        </span>
        <div className="best-odds-bookmaker-filter__actions">
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleSelectAll}
            disabled={isAllSelected}
          >
            {t('surebets.viewFilters.championshipsSelectAll')}
          </button>
          <button
            type="button"
            className="best-odds-bookmaker-filter__action-btn"
            onClick={handleDeselectAll}
            disabled={Array.isArray(selectedChampionships) && selectedChampionships.length === 0}
          >
            {t('surebets.viewFilters.championshipsDeselectAll')}
          </button>
        </div>
      </div>

      <div className="best-odds-bookmaker-filter__search">
        <input
          type="text"
          className="best-odds-bookmaker-filter__search-input"
          value={localSearch}
          onChange={(event) => setLocalSearch(event.target.value)}
          placeholder={t('surebets.viewFilters.championshipsSearchPlaceholder')}
          aria-label={t('surebets.viewFilters.championshipsSearchPlaceholder')}
          autoComplete="off"
        />
        {localSearch.trim() && (
          <button
            type="button"
            className="best-odds-bookmaker-filter__search-clear"
            onClick={() => setLocalSearch('')}
            aria-label={t('surebets.viewFilters.searchClearAriaLabel')}
          >
            ×
          </button>
        )}
      </div>

      <div className="best-odds-bookmaker-filter__grid">
        {filteredChampionships.map((championship) => {
          const checked = isChampionshipSelected(championship);
          return (
            <label
              key={championship}
              className={`best-odds-bookmaker-filter__item${checked ? ' is-selected' : ''}`}
            >
              <input
                type="checkbox"
                className="best-odds-bookmaker-filter__checkbox"
                checked={checked}
                onChange={() => handleToggle(championship)}
                value={championship}
              />
              <span className="best-odds-bookmaker-filter__name" title={championship}>
                {championship}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

SurebetViewChampionshipFilter.propTypes = {
  className: PropTypes.string
};

export default SurebetViewChampionshipFilter;
